import React from 'react';
import DataQualityView from './DataQualityView';
import { useDataQualityController } from './use-data-quality-controller';

interface ErrorBoundaryState {
  hasError: boolean;
  message?: string;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    console.error('DataQualityRoute error boundary caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="data-quality-hub error-boundary" style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, marginBottom: '1rem' }}>
          <div style={{ fontWeight: 600, color: '#b91c1c' }}>A panel failed to render.</div>
          <div style={{ fontSize: '0.9rem', color: '#7f1d1d' }}>{this.state.message}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

interface DataQualityRouteProps {
  className?: string;
}

const DataQualityRoute: React.FC<DataQualityRouteProps> = ({ className = '' }) => {
  const controller = useDataQualityController();

  return (
    <ErrorBoundary>
      <DataQualityView
        className={className}
        searchTerm={controller.searchTerm}
        onSearchTermChange={controller.setSearchTerm}
        committedSearch={controller.committedSearch}
        onSearchSubmit={controller.applySearch}
        onSearchClear={controller.clearSearch}
        filters={controller.filters}
        onFiltersChange={controller.updateFilters}
        pagination={{
          page: controller.page,
          pageSize: controller.pageSize,
          totalCount: controller.query.data?.total_count ?? 0,
          currentCount: controller.query.data?.programs.length ?? 0,
          onPageChange: controller.setPage,
          onPageSizeChange: controller.setPageSize
        }}
        results={controller.query.data}
        isLoading={controller.query.isLoading}
        isFetching={controller.query.isFetching}
        error={controller.query.error}
        refetch={controller.query.refetch}
        selectedProgramIds={controller.selectedProgramIds}
        setSelectedProgramIds={controller.setSelectedProgramIds}
        selectedSearchId={controller.selectedSearchId}
        setSelectedSearchId={controller.setSelectedSearchId}
        startPrintJob={controller.startPrintJob}
        activeCategoryFilter={controller.activeCategoryFilter}
        onClearCategoryFilter={controller.clearCategoryFilter}
      />
    </ErrorBoundary>
  );
};

export default DataQualityRoute;
